import {ChevronDown, Menu, TrendingUp, X} from 'lucide-react'
import { useState } from "react"

interface HeaderProps {
  currentPath: string
  navigate: (path: string) => void
}

const calculatorLinks = [
  { label: "Master ROAS", path: "/", hint: "Core spend vs. revenue" },
  { label: "Break-Even ROAS", path: "/calculators/break-even", hint: "Minimum profitable ROAS" },
  { label: "Google Ads ROAS", path: "/calculators/google-ads", hint: "CPC and conversion model" },
  { label: "Facebook Ads ROAS", path: "/calculators/facebook-ads", hint: "CTR and paid social" },
  { label: "ACoS Converter", path: "/calculators/acos-converter", hint: "Amazon ACoS to ROAS" },
]

const navLinks = [
  { label: "Blog", path: "/blog" },
  { label: "FAQ", path: "/faq" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
]

function Header({ currentPath, navigate }: HeaderProps) {
  const [calcOpen, setCalcOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  const isCalcActive = currentPath.startsWith("/calculators") || currentPath === "/"

  const go = (path: string) => {
    navigate(path)
    setMobileOpen(false)
    setCalcOpen(false)
  }

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-[4.25rem]">
          <button onClick={() => go("/")} className="flex items-center gap-2.5 shrink-0 group">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600 text-white shadow-sm shadow-emerald-600/20 group-hover:bg-emerald-700 transition-colors">
              <TrendingUp className="w-4 h-4" strokeWidth={2.5} />
            </span>
            <span className="text-[15px] font-extrabold tracking-tight text-slate-900">
              AdsROAS
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-0.5">
            <div className="relative" onMouseEnter={() => setCalcOpen(true)} onMouseLeave={() => setCalcOpen(false)}>
              <button
                className={`flex items-center gap-1 px-3 py-2 rounded-md text-[13px] font-semibold tracking-wide transition-colors ${
                  isCalcActive
                    ? "text-emerald-800 bg-emerald-50"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
              >
                Calculators
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${calcOpen ? "rotate-180" : ""}`} />
              </button>
              {calcOpen ? (
                <div className="absolute top-full left-0 pt-2 w-72">
                  <div className="bg-white rounded-xl shadow-xl shadow-slate-900/8 border border-slate-200 py-2 overflow-hidden">
                    {calculatorLinks.map((link) => (
                      <button
                        key={link.path}
                        onClick={() => go(link.path)}
                        className={`w-full text-left px-4 py-2.5 transition-colors ${
                          currentPath === link.path ? "bg-emerald-50" : "hover:bg-slate-50"
                        }`}
                      >
                        <span className={`block text-[13px] font-semibold ${currentPath === link.path ? "text-emerald-800" : "text-slate-800"}`}>
                          {link.label}
                        </span>
                        <span className="block text-[11px] text-slate-500 mt-0.5">{link.hint}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            {navLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => go(link.path)}
                className={`px-3 py-2 rounded-md text-[13px] font-semibold tracking-wide transition-colors ${
                  currentPath === link.path
                    ? "text-emerald-800 bg-emerald-50"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <button
            onClick={() => go("/")}
            className="hidden md:inline-flex items-center bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-semibold px-4 py-2 rounded-md shadow-sm shadow-emerald-600/20 transition-colors"
          >
            Open Calculator
          </button>

          <button className="md:hidden p-2 text-slate-700" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen ? (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400 px-2 pt-1 pb-2">Calculators</p>
          {calculatorLinks.map((link) => (
            <button
              key={link.path}
              onClick={() => go(link.path)}
              className={`block w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold ${
                currentPath === link.path ? "text-emerald-800 bg-emerald-50" : "text-slate-700"
              }`}
            >
              {link.label}
            </button>
          ))}
          <div className="border-t border-slate-100 my-3" />
          {navLinks.map((link) => (
            <button
              key={link.path}
              onClick={() => go(link.path)}
              className={`block w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold ${
                currentPath === link.path ? "text-emerald-800 bg-emerald-50" : "text-slate-700"
              }`}
            >
              {link.label}
            </button>
          ))}
        </div>
      ) : null}
    </header>
  )
}

export default Header
